import { useState } from "react";
import type { BlockType, EditProps, RenderProps } from "./types";
import { InlineRichText } from "./inline-text";
import { escapeHtml, newBlockId } from "./utils";

/* =============================================================================
 * Course-specific blocks — the reusable, centrally-designed pedagogy widgets.
 * Change a component here and every course/lesson updates at once.
 *
 * NOTE on InlineQuiz: this is a FORMATIVE self-check (ungraded, instant
 * feedback, never persisted). Its correct answers live in the lesson's block
 * JSON, which is acceptable for low-stakes practice. Graded checkpoint quizzes
 * are a separate server-graded entity (see quizzes table) whose answers never
 * reach the client before submission. `toHtml` here deliberately omits the
 * correctness markers from the static SEO snapshot.
 * ========================================================================== */

/* -------------------------------------------------------------------------- */
/* Learning objectives                                                         */
/* -------------------------------------------------------------------------- */

interface ObjectivesData {
  title: string;
  items: string[];
}

const ObjectivesRender = ({ data }: RenderProps<ObjectivesData>) => (
  <section className="vu-objectives">
    <p className="vu-objectives-title">{data.title || "What you'll learn"}</p>
    <ul className="vu-objectives-list">
      {data.items.filter(Boolean).map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  </section>
);

const ObjectivesEdit = ({ data, onChange }: EditProps<ObjectivesData>) => (
  <section className="vu-objectives">
    <input
      type="text"
      className="vu-callout-title-input"
      placeholder="What you'll learn"
      value={data.title}
      onChange={(e) => onChange({ ...data, title: e.target.value })}
    />
    <ul className="vu-objectives-list">
      {data.items.map((it, i) => (
        <li key={i} className="flex items-center gap-2">
          <input
            type="text"
            className="auth-input flex-1"
            placeholder={`Objective ${i + 1}`}
            value={it}
            onChange={(e) => {
              const items = data.items.slice();
              items[i] = e.target.value;
              onChange({ ...data, items });
            }}
          />
          <button
            type="button"
            className="tt-btn"
            aria-label="Remove objective"
            onClick={() => onChange({ ...data, items: data.items.filter((_, j) => j !== i) })}
          >
            ✕
          </button>
        </li>
      ))}
    </ul>
    <button type="button" className="tt-btn mt-2" onClick={() => onChange({ ...data, items: [...data.items, ""] })}>
      + Add objective
    </button>
  </section>
);

export const LearningObjectives: BlockType<ObjectivesData> = {
  type: "learning-objectives",
  label: "Objectives",
  icon: "✦",
  group: "text",
  description: "A checklist of what the learner will be able to do.",
  defaultData: () => ({ title: "What you'll learn", items: ["", ""] }),
  Render: ObjectivesRender,
  Edit: ObjectivesEdit,
  toHtml: (d) =>
    `<section class="vu-objectives"><p class="vu-objectives-title">${escapeHtml(
      d.title || "What you'll learn",
    )}</p><ul class="vu-objectives-list">${d.items
      .filter(Boolean)
      .map((it) => `<li>${escapeHtml(it)}</li>`)
      .join("")}</ul></section>`,
  toText: (d) => `${d.title}: ${d.items.filter(Boolean).join("; ")}`,
};

/* -------------------------------------------------------------------------- */
/* Key terms / glossary                                                        */
/* -------------------------------------------------------------------------- */

interface KeyTermsData {
  title: string;
  terms: { term: string; definition: string }[];
}

const KeyTermsRender = ({ data }: RenderProps<KeyTermsData>) => (
  <section className="vu-keyterms">
    {data.title && <p className="vu-keyterms-title">{data.title}</p>}
    <dl className="vu-keyterms-list">
      {data.terms
        .filter((t) => t.term)
        .map((t, i) => (
          <div className="vu-keyterm" key={i}>
            <dt>{t.term}</dt>
            <dd>{t.definition}</dd>
          </div>
        ))}
    </dl>
  </section>
);

const KeyTermsEdit = ({ data, onChange }: EditProps<KeyTermsData>) => (
  <section className="vu-keyterms">
    <input
      type="text"
      className="vu-callout-title-input"
      placeholder="Key terms"
      value={data.title}
      onChange={(e) => onChange({ ...data, title: e.target.value })}
    />
    <div className="flex flex-col gap-2">
      {data.terms.map((t, i) => (
        <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center">
          <input
            type="text"
            className="auth-input"
            placeholder="Term (e.g. svara)"
            value={t.term}
            onChange={(e) => {
              const terms = data.terms.slice();
              terms[i] = { ...terms[i]!, term: e.target.value };
              onChange({ ...data, terms });
            }}
          />
          <input
            type="text"
            className="auth-input"
            placeholder="Definition"
            value={t.definition}
            onChange={(e) => {
              const terms = data.terms.slice();
              terms[i] = { ...terms[i]!, definition: e.target.value };
              onChange({ ...data, terms });
            }}
          />
          <button
            type="button"
            className="tt-btn"
            aria-label="Remove term"
            onClick={() => onChange({ ...data, terms: data.terms.filter((_, j) => j !== i) })}
          >
            ✕
          </button>
        </div>
      ))}
    </div>
    <button
      type="button"
      className="tt-btn mt-2"
      onClick={() => onChange({ ...data, terms: [...data.terms, { term: "", definition: "" }] })}
    >
      + Add term
    </button>
  </section>
);

export const KeyTerms: BlockType<KeyTermsData> = {
  type: "key-terms",
  label: "Key terms",
  icon: "📖",
  group: "text",
  description: "A glossary of terms and definitions.",
  defaultData: () => ({ title: "Key terms", terms: [{ term: "", definition: "" }] }),
  Render: KeyTermsRender,
  Edit: KeyTermsEdit,
  toHtml: (d) =>
    `<section class="vu-keyterms">${d.title ? `<p class="vu-keyterms-title">${escapeHtml(d.title)}</p>` : ""}<dl class="vu-keyterms-list">${d.terms
      .filter((t) => t.term)
      .map((t) => `<div class="vu-keyterm"><dt>${escapeHtml(t.term)}</dt><dd>${escapeHtml(t.definition)}</dd></div>`)
      .join("")}</dl></section>`,
  toText: (d) => d.terms.map((t) => `${t.term}: ${t.definition}`).join("; "),
};

/* -------------------------------------------------------------------------- */
/* Exercise / practice prompt                                                  */
/* -------------------------------------------------------------------------- */

interface ExerciseData {
  title: string;
  html: string;
  hint: string;
}

const ExerciseRender = ({ data }: RenderProps<ExerciseData>) => (
  <section className="vu-exercise">
    <p className="vu-exercise-label">{data.title || "Practice"}</p>
    <div dangerouslySetInnerHTML={{ __html: data.html }} />
    {data.hint && (
      <details className="vu-exercise-hint">
        <summary>Show a hint</summary>
        <div dangerouslySetInnerHTML={{ __html: data.hint }} />
      </details>
    )}
  </section>
);

const ExerciseEdit = ({ data, onChange }: EditProps<ExerciseData>) => (
  <section className="vu-exercise">
    <input
      type="text"
      className="vu-callout-title-input"
      placeholder="Practice"
      value={data.title}
      onChange={(e) => onChange({ ...data, title: e.target.value })}
    />
    <InlineRichText html={data.html} onUpdate={(html) => onChange({ ...data, html })} placeholder="Describe the exercise" multiline />
    <p className="auth-label mt-3">Hint (optional, collapsible)</p>
    <InlineRichText html={data.hint} onUpdate={(hint) => onChange({ ...data, hint })} placeholder="A hint the learner can reveal" multiline />
  </section>
);

export const Exercise: BlockType<ExerciseData> = {
  type: "exercise",
  label: "Exercise",
  icon: "✎",
  group: "text",
  description: "A practice task, with an optional reveal-able hint.",
  defaultData: () => ({ title: "Practice", html: "", hint: "" }),
  Render: ExerciseRender,
  Edit: ExerciseEdit,
  toHtml: (d) =>
    `<section class="vu-exercise"><p class="vu-exercise-label">${escapeHtml(d.title || "Practice")}</p>${d.html || ""}${
      d.hint ? `<details class="vu-exercise-hint"><summary>Show a hint</summary>${d.hint}</details>` : ""
    }</section>`,
};

/* -------------------------------------------------------------------------- */
/* Inline quiz (formative self-check, ephemeral)                               */
/* -------------------------------------------------------------------------- */

type InlineQuizType = "single_choice" | "multi_choice" | "true_false";

interface InlineQuizOption {
  id: string;
  label: string;
  correct: boolean;
}

interface InlineQuizData {
  question: string;
  type: InlineQuizType;
  options: InlineQuizOption[];
  explanation: string;
}

const InlineQuizRender = ({ data }: RenderProps<InlineQuizData>) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [checked, setChecked] = useState(false);
  const multi = data.type === "multi_choice";

  const toggle = (id: string) => {
    if (checked) return;
    if (multi) setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
    else setSelected([id]);
  };

  const correctIds = data.options.filter((o) => o.correct).map((o) => o.id);
  const isRight =
    checked &&
    selected.length === correctIds.length &&
    selected.every((s) => correctIds.includes(s));

  return (
    <aside className="vu-inline-quiz" aria-label="Self-check">
      <p className="vu-inline-quiz-eyebrow">Check yourself</p>
      <p className="vu-inline-quiz-q">{data.question || "Question"}</p>
      <ul className="vu-inline-quiz-options">
        {data.options.map((o) => {
          const isSel = selected.includes(o.id);
          const state = checked ? (o.correct ? "correct" : isSel ? "wrong" : "") : isSel ? "selected" : "";
          return (
            <li key={o.id}>
              <button type="button" className={`vu-inline-quiz-opt ${state}`} onClick={() => toggle(o.id)} disabled={checked}>
                <span className="vu-inline-quiz-marker" aria-hidden>
                  {checked ? (o.correct ? "✓" : isSel ? "✕" : "") : multi ? (isSel ? "■" : "□") : isSel ? "●" : "○"}
                </span>
                <span>{o.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="vu-inline-quiz-actions">
        {!checked ? (
          <button type="button" className="btn-app" onClick={() => setChecked(true)} disabled={selected.length === 0}>
            Check
          </button>
        ) : (
          <>
            <p className={`vu-inline-quiz-verdict ${isRight ? "is-right" : "is-wrong"}`}>
              {isRight ? "Correct" : "Not quite"}
            </p>
            <button
              type="button"
              className="btn-app-ghost"
              onClick={() => {
                setChecked(false);
                setSelected([]);
              }}
            >
              Try again
            </button>
          </>
        )}
      </div>
      {checked && data.explanation && (
        <div className="vu-inline-quiz-explain" dangerouslySetInnerHTML={{ __html: data.explanation }} />
      )}
    </aside>
  );
};

const InlineQuizEdit = ({ data, onChange }: EditProps<InlineQuizData>) => {
  const setOption = (id: string, patch: Partial<InlineQuizOption>) =>
    onChange({ ...data, options: data.options.map((o) => (o.id === id ? { ...o, ...patch } : o)) });

  const setCorrect = (id: string) => {
    if (data.type === "multi_choice") {
      onChange({ ...data, options: data.options.map((o) => (o.id === id ? { ...o, correct: !o.correct } : o)) });
    } else {
      onChange({ ...data, options: data.options.map((o) => ({ ...o, correct: o.id === id })) });
    }
  };

  return (
    <aside className="vu-inline-quiz">
      <p className="vu-inline-quiz-eyebrow">Self-check (instant feedback, not graded)</p>
      <div className="block-meta-row">
        {(["single_choice", "multi_choice", "true_false"] as const).map((t) => (
          <button
            key={t}
            type="button"
            className={`tt-btn ${data.type === t ? "is-active" : ""}`}
            onClick={() => {
              if (t === "true_false") {
                onChange({
                  ...data,
                  type: t,
                  options: [
                    { id: newBlockId(), label: "True", correct: true },
                    { id: newBlockId(), label: "False", correct: false },
                  ],
                });
              } else {
                onChange({ ...data, type: t });
              }
            }}
          >
            {t === "single_choice" ? "Single" : t === "multi_choice" ? "Multiple" : "True / False"}
          </button>
        ))}
      </div>
      <input
        type="text"
        className="auth-input mt-2"
        placeholder="Question"
        value={data.question}
        onChange={(e) => onChange({ ...data, question: e.target.value })}
      />
      <ul className="flex flex-col gap-2 mt-3">
        {data.options.map((o) => (
          <li key={o.id} className="grid grid-cols-[auto_1fr_auto] gap-2 items-center">
            <button
              type="button"
              className={`tt-btn ${o.correct ? "is-active" : ""}`}
              title="Mark correct"
              onClick={() => setCorrect(o.id)}
            >
              {o.correct ? "✓ correct" : "mark"}
            </button>
            <input
              type="text"
              className="auth-input"
              placeholder="Option"
              value={o.label}
              disabled={data.type === "true_false"}
              onChange={(e) => setOption(o.id, { label: e.target.value })}
            />
            {data.type !== "true_false" && (
              <button
                type="button"
                className="tt-btn"
                aria-label="Remove option"
                onClick={() => onChange({ ...data, options: data.options.filter((x) => x.id !== o.id) })}
              >
                ✕
              </button>
            )}
          </li>
        ))}
      </ul>
      {data.type !== "true_false" && (
        <button
          type="button"
          className="tt-btn mt-2"
          onClick={() => onChange({ ...data, options: [...data.options, { id: newBlockId(), label: "", correct: false }] })}
        >
          + Add option
        </button>
      )}
      <p className="auth-label mt-3">Explanation (shown after checking)</p>
      <InlineRichText
        html={data.explanation}
        onUpdate={(explanation) => onChange({ ...data, explanation })}
        placeholder="Why the answer is what it is"
        multiline
      />
    </aside>
  );
};

export const InlineQuiz: BlockType<InlineQuizData> = {
  type: "inline-quiz",
  label: "Self-check",
  icon: "?",
  group: "special",
  description: "An inline multiple-choice check with instant feedback.",
  defaultData: () => ({
    question: "",
    type: "single_choice",
    options: [
      { id: newBlockId(), label: "", correct: true },
      { id: newBlockId(), label: "", correct: false },
    ],
    explanation: "",
  }),
  Render: InlineQuizRender,
  Edit: InlineQuizEdit,
  // Static snapshot deliberately omits which option is correct.
  toHtml: (d) =>
    `<aside class="vu-inline-quiz"><p class="vu-inline-quiz-eyebrow">Check yourself</p><p class="vu-inline-quiz-q">${escapeHtml(
      d.question,
    )}</p><ul class="vu-inline-quiz-options">${d.options
      .map((o) => `<li><span class="vu-inline-quiz-opt">${escapeHtml(o.label)}</span></li>`)
      .join("")}</ul></aside>`,
  toText: (d) => d.question,
};
